import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useUsers } from "@/api/queries/users";

// #292 (UC-2): GENERISCHER Verantwortlichen-Dialog für Artefakte (Marketplace,
// Workspace-Vorlage) — das Connector-Pendant bleibt vorerst eigenständig (#279 hat dort
// eigene Übergabe-Regeln; Umstellung auf diesen Dialog = Notiz, nicht dieses Ticket).
// „System (alle Admins)" = ownerlos (ownerId null) — der Default aller Admin-Artefakte.

const SYSTEM = "__system__";

export function SetOwnerDialog({
  open,
  onOpenChange,
  artifactLabel,
  currentOwnerId,
  isPending,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** z.B. „Marketplace \"mk\"" — nur für die Beschriftung. */
  artifactLabel: string;
  currentOwnerId: string | null;
  isPending: boolean;
  onSave: (ownerId: string | null) => void;
}) {
  const users = useUsers();
  const [selected, setSelected] = useState<string>(currentOwnerId ?? SYSTEM);

  useEffect(() => {
    if (open) setSelected(currentOwnerId ?? SYSTEM);
  }, [open, currentOwnerId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="set-owner-dialog">
        <DialogHeader>
          <DialogTitle>Verantwortlichen festlegen</DialogTitle>
          <DialogDescription>
            Wer verwaltet {artifactLabel} fachlich? Der Verantwortliche darf auch{" "}
            <strong>Entwürfe dieses Artefakts freigeben</strong> — das ist eine Berechtigung,
            keine Notiz. Ohne Verantwortlichen gehört es dem System, dann entscheiden alle Admins.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1">
          <Label>Verantwortlich</Label>
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger data-testid="owner-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={SYSTEM}>System (alle Admins)</SelectItem>
              {(users.data ?? []).map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  {u.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {users.isError && (
            <p className="text-[11.5px] text-danger">User-Liste konnte nicht geladen werden.</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Abbrechen
          </Button>
          <Button
            onClick={() => onSave(selected === SYSTEM ? null : selected)}
            disabled={isPending}
            data-testid="owner-save"
          >
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Anzeige-Wert für die PropGrid-Zeile „Verantwortlich": User verlinkt (Email), ownerlos
// = „System (alle Admins)", nicht auflösbarer User = orphaned-Badge (Zuweisung existiert).
// #292 (Review MED-4): `undefined` heißt „nicht geliefert" (Liste/älterer Master) — daraus
// darf KEINE positive „System"-Aussage werden. Hinweis (Review LOW-13): useUsers pollt
// alle 15s — für die Admin-Detailseiten bewusst akzeptiert (kleine Liste, ein Endpoint).
export function OwnerValue({ ownerId }: { ownerId: string | null | undefined }) {
  const users = useUsers();
  if (ownerId === undefined) return <span className="text-text3">—</span>;
  if (ownerId === null) return <span className="text-text3">System (alle Admins)</span>;
  const user = (users.data ?? []).find((u) => u.id === ownerId);
  if (user) {
    return (
      <Link to={`/users/${encodeURIComponent(ownerId)}`} className="text-accent-text hover:underline">
        {user.email}
      </Link>
    );
  }
  if (users.isLoading || users.isError) return <span className="mono text-xs">{ownerId}</span>;
  return <Badge variant="orphaned">orphaned: {ownerId}</Badge>;
}
